import React, { useEffect, useMemo, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import { learningPathsApi, orgApi } from '../../api/client';

type Props = { storeId?: string | null; onBack?: () => void };

function daysUntil(value: string) {
  const expiration = new Date(value);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  expiration.setHours(0, 0, 0, 0);
  return Math.ceil((expiration.getTime() - today.getTime()) / 86400000);
}

function formatDate(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function statusChip(expirationDate?: string | null) {
  if (!expirationDate) return <Chip size="small" label="—" variant="outlined" />;
  const days = daysUntil(expirationDate);
  if (days <= 0) return <Chip size="small" icon={<ErrorIcon />} label="Expired" color="error" />;
  if (days <= 7) return <Chip size="small" icon={<WarningAmberIcon />} label={`Expires in ${days} days`} color="error" variant="outlined" />;
  if (days <= 30) return <Chip size="small" icon={<WarningAmberIcon />} label={`Expires in ${days} days`} color="warning" variant="outlined" />;
  return <Chip size="small" icon={<CheckCircleIcon />} label="Valid" color="success" variant="outlined" />;
}

const CertificationCompliancePage: React.FC<Props> = ({ storeId, onBack }) => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const [assignments, employees] = await Promise.all([
          learningPathsApi.listAssignments(),
          orgApi.listEmployees(storeId ? { storeId } : undefined),
        ]);
        const allowed = new Set((employees as any[]).map((employee) => employee.id));
        const certifications = (assignments as any[])
          .filter((item) => !storeId || allowed.has(item.employeeId))
          .map((item) => {
            const path = item.learningPath;
            let expirationDate = item.expirationDate;
            if (!expirationDate && path?.isCertification && item.completedDate && path.certificationExpirationDays) {
              const expiration = new Date(item.completedDate);
              expiration.setDate(expiration.getDate() + Number(path.certificationExpirationDays));
              expirationDate = expiration.toISOString();
            }
            return { ...item, expirationDate };
          })
          .filter((item) => item.learningPath?.isCertification || item.expirationDate || item.status === 'expired');
        setRows(certifications);
      } catch (err: any) {
        setError(err?.response?.data?.error || err.message || 'Failed to load certification data');
      } finally {
        setLoading(false);
      }
    })();
  }, [storeId]);

  const groups = useMemo(() => {
    const expired = rows.filter((item) => item.status === 'expired' || (item.expirationDate && daysUntil(item.expirationDate) <= 0));
    const expiringSoon = rows.filter((item) => item.expirationDate && daysUntil(item.expirationDate) > 0 && daysUntil(item.expirationDate) <= 30 && item.status !== 'expired');
    const valid = rows.filter((item) => item.expirationDate && daysUntil(item.expirationDate) > 30 && item.status !== 'expired');
    return [expiringSoon, expired, valid];
  }, [rows]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;
  }

  const current = groups[tab];

  return (
    <Box>
      {onBack && <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>Back to Dashboard</Button>}
      <Typography variant="h4" fontWeight={700} gutterBottom>Certification Compliance</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        View certifications that are expiring soon, already expired, or currently valid. The system sends reminders at 30, 14, and 7 days and auto re-assigns when expired.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
        <Tab label={`Expiring soon (${groups[0].length})`} />
        <Tab label={`Expired (${groups[1].length})`} />
        <Tab label={`Valid (${groups[2].length})`} />
      </Tabs>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Employee', 'Certification', 'Completed', 'Expiration', 'Status'].map((column) => (
                <TableCell key={column}>{column}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {current.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    {tab === 0 && (
                      <>
                        <EventBusyIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                        <Typography color="text.secondary">No certifications expiring in the next 30 days.</Typography>
                      </>
                    )}
                    {tab === 1 && (
                      <>
                        <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
                        <Typography color="text.secondary">No expired certifications.</Typography>
                      </>
                    )}
                    {tab === 2 && <Typography color="text.secondary">No valid certifications in this view.</Typography>}
                  </Box>
                </TableCell>
              </TableRow>
            ) : current.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{item.employee?.name || '—'}</Typography>
                  <Typography variant="caption" color="text.secondary">{item.employee?.email || item.employeeId}</Typography>
                </TableCell>
                <TableCell>{item.learningPath?.title || 'Certification'}</TableCell>
                <TableCell>{formatDate(item.completedDate)}</TableCell>
                <TableCell>{formatDate(item.expirationDate)}</TableCell>
                <TableCell>{statusChip(item.expirationDate)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CertificationCompliancePage;
